import {
  AuditLogFilterOptions,
  AuditLogFilters,
  AuditLogQueryResponse,
} from '../types/AuditLog';
import { apiClient } from './sessionAwareApiClient';
import { ApiError } from './apiClient';
import { notificationService } from './notificationService';

/**
 * Converts a local wall-clock date-time to the equivalent UTC wall-clock string.
 *
 * The date pickers produce calendar days in the reader's own timezone, but audit timestamps are
 * stored in UTC and compared as raw values. Sending the bare date would apply a UTC day boundary
 * to a local day: an Eastern PSAP filtering "today" would lose everything after 8pm local and
 * silently pick up the previous evening instead — night-shift activity, which is exactly what an
 * access review is usually looking for.
 *
 * The trailing `Z` is deliberately dropped. ASP.NET Core binds an offset-bearing string by
 * converting it to server-local time, which is only harmless for as long as the server runs in UTC;
 * an offset-less string binds as the exact value written here.
 */
function toUtcWallClock(localDateTime: string): string {
  const parsed = new Date(localDateTime);
  if (Number.isNaN(parsed.getTime())) return localDateTime;
  return parsed.toISOString().slice(0, 23);
}

function toParams(filters: AuditLogFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.startDate) params.append('startDate', toUtcWallClock(`${filters.startDate}T00:00:00.000`));
  if (filters.endDate) params.append('endDate', toUtcWallClock(`${filters.endDate}T23:59:59.999`));
  if (filters.userId) params.append('userId', filters.userId);
  if (filters.userName) params.append('userName', filters.userName);
  if (filters.action) params.append('action', filters.action);
  if (filters.resourceType) params.append('resourceType', filters.resourceType);
  if (filters.resourceId) params.append('resourceId', filters.resourceId);
  if (filters.result) params.append('result', filters.result);
  return params;
}

export class AuditService {
  /**
   * @param maxAuditLogId Snapshot ceiling from the first page of this filter, or null to start a
   * new snapshot. See AuditLogQueryResponse.snapshotMaxId.
   */
  static async queryAuditLog(
    filters: AuditLogFilters,
    page: number,
    pageSize: number,
    maxAuditLogId: number | null = null
  ): Promise<AuditLogQueryResponse> {
    try {
      const params = toParams(filters);
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      if (maxAuditLogId !== null) params.append('maxAuditLogId', maxAuditLogId.toString());

      const response = await apiClient.get<AuditLogQueryResponse>(`/api/audit?${params}`);
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Audit Trail');
      throw _error;
    }
  }

  static async getFilterOptions(): Promise<AuditLogFilterOptions> {
    try {
      const response = await apiClient.get<AuditLogFilterOptions>('/api/audit/filter-options');
      return response.data;
    } catch (_error) {
      notificationService.handleApiError(_error as ApiError, 'Audit Filter Options');
      throw _error;
    }
  }

  /**
   * Downloads the filtered audit trail as CSV.
   *
   * Errors are rethrown without going through notificationService: the server refuses
   * oversized exports with a specific, actionable message ("narrow the date range"), and the
   * caller renders that inline rather than as a generic toast.
   */
  static async exportAuditLog(filters: AuditLogFilters): Promise<Blob> {
    const response = await apiClient.get(`/api/audit/export?${toParams(filters)}`, {
      responseType: 'blob',
    });
    return response.data;
  }
}
